import { CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableRow } from "@material-ui/core";
import { useState, Fragment, useEffect } from 'react';
import ArtApi from "../../api/ArtApi";
import IEventDetails from "../../models/EventDetails"
import UserInfo from "../../components/UserInfo";
import Moment from "react-moment";
import { RouteComponentProps, useLocation } from "react-router";
import { CommentsTable } from "../../components/CommentsTable";
import { TaggingChart } from "../../components/EventChart";
import { LabelledEvents } from "../../components/LabelledEvents";
import ReviewerStatus from "../../components/ReviewerStatus";
import { DownloadMsgPack } from "../../components/DownloadMsgPack";
import { ReviewStatus } from "../../models/IReviewer";
import { useAuthDataContext } from "../../components/AuthDataProvider";

type WaterEventDetailsProps = RouteComponentProps<{
    waterEventId: string;
}>;


export const WaterEventDetails = (props: WaterEventDetailsProps) => {
    const { user } = useAuthDataContext();

    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloadable, setIsDownloadable] = useState(false);
    const [waterEvent, setWaterEvent] = useState<IEventDetails | null>(null);
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const [showSubCopiedMessage, setShowSubCopiedMessage] = useState(false);


    // A custom hook that builds on useLocation to parse
    // the query string for you.
    function useQuery() {
    return new URLSearchParams(useLocation().search);
    }

    let query = useQuery();
    const forceReview = query.get("forceReview")

    useEffect(() => {
        setIsLoading(true);
        ArtApi.getWaterEvent(props.match.params.waterEventId, forceReview)
            .then(response => {
                setIsLoading(false);
                setWaterEvent(response.response);
                setError(response.error);
            }).catch(error => {
                setIsLoading(false);
                setError(error.message);
            })
    }, [props.match.params.waterEventId, forceReview]);

    useEffect(() => {
        if (waterEvent === null) {
            setIsDownloadable(false);
        } else {
            setIsDownloadable(waterEvent.completed)
        }
    }, [waterEvent]);


    const closeReview = (status: ReviewStatus) => {

        let currWaterEvent = Object.assign({}, waterEvent);
        if(currWaterEvent?.reviewers){
            for (const reviewer of currWaterEvent?.reviewers) {
                if(reviewer.reviewer === user?.user_details.user_id){
                    reviewer.status = status
                    break;
                }
            }
            setWaterEvent(currWaterEvent);
        }
    }

    const handleCopyDeviceId = () => {
        if (waterEvent) {
            navigator.clipboard.writeText(waterEvent.deviceId);
            setShowCopiedMessage(true);
            setTimeout(() => setShowCopiedMessage(false), 2000);
        }
    }

    const handleCopySubMeterId = () => {
        if (waterEvent && waterEvent.subMeterId) {
            navigator.clipboard.writeText(waterEvent.subMeterId);
            setShowSubCopiedMessage(true);
            setTimeout(() => setShowSubCopiedMessage(false), 2000);
        }
    }

    return (
        <Fragment>
            <h1>Water Event</h1>
            {isLoading && <CircularProgress color="secondary" />}
            {error}
            <Grid container spacing={3}>
                <Grid item>
                    <h2>Details</h2>
                    <Paper className="paper">
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell>
                                        Core ID
                                    </TableCell>
                                    <TableCell>
                                        {waterEvent && (
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                <span 
                                                    style={{ 
                                                        cursor: 'pointer', 
                                                        borderBottom: '1px dashed #ccc',
                                                        padding: '2px 4px',
                                                        borderRadius: '3px'
                                                    }}
                                                    onClick={handleCopyDeviceId}
                                                    title="Click to copy to clipboard"
                                                >
                                                    {waterEvent.deviceId} 📋
                                                </span>
                                                {showCopiedMessage && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '-30px',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        backgroundColor: '#333',
                                                        color: 'white',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 1000,
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                    }}>
                                                        Copied to clipboard!
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 0,
                                                            height: 0,
                                                            borderLeft: '4px solid transparent',
                                                            borderRight: '4px solid transparent',
                                                            borderTop: '4px solid #333'
                                                        }}></div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Device Data
                                    </TableCell>
                                    <TableCell>
                                        {waterEvent && (
                                            <>
                                                <a href={"https://mfgweb.alertlabs.com/device/" + waterEvent.deviceId} target="_blank" rel="noopener noreferrer">
                                                    MFGWeb
                                                </a>
                                                {" | "}
                                                <a href={"https://fleetweb.alertlabs.com/device/" + waterEvent.deviceId} target="_blank" rel="noopener noreferrer">
                                                    FleetWeb
                                                </a>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                                {waterEvent && waterEvent.subMeterId && (
                                    <>
                                        <TableRow>
                                            <TableCell>
                                                Paired Core ID
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                    <span
                                                        style={{
                                                            cursor: 'pointer',
                                                            borderBottom: '1px dashed #ccc',
                                                            padding: '2px 4px',
                                                            borderRadius: '3px'
                                                        }}
                                                        onClick={handleCopySubMeterId}
                                                        title="Click to copy to clipboard"
                                                    >
                                                        {waterEvent.subMeterId} 📋
                                                    </span>
                                                    {showSubCopiedMessage && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '-30px',
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            backgroundColor: '#333',
                                                            color: 'white',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                            whiteSpace: 'nowrap',
                                                            zIndex: 1000,
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                        }}>
                                                            Copied to clipboard!
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                width: 0,
                                                                height: 0,
                                                                borderLeft: '4px solid transparent',
                                                                borderRight: '4px solid transparent',
                                                                borderTop: '4px solid #333'
                                                            }}></div>
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                Paired Device Data
                                            </TableCell>
                                            <TableCell>
                                                <a href={"https://mfgweb.alertlabs.com/device/" + waterEvent.subMeterId} target="_blank" rel="noopener noreferrer">
                                                    MFGWeb
                                                </a>
                                                {" | "}
                                                <a href={"https://fleetweb.alertlabs.com/device/" + waterEvent.subMeterId} target="_blank" rel="noopener noreferrer">
                                                    FleetWeb
                                                </a>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                                <TableRow>
                                    <TableCell>
                                        Reported By
                                    </TableCell>
                                    <TableCell>
                                        {waterEvent ? <UserInfo userId={waterEvent.reportedBy} /> : "-"}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Priming Start Date
                                    </TableCell>
                                    <TableCell>
                                        {waterEvent ? <Moment format="YYYY-MM-DD">{waterEvent.primingStartDate}</Moment> : "-"}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Region of Interest Start Date
                                    </TableCell>
                                    <TableCell>
                                        {waterEvent ? <Moment format="YYYY-MM-DD">{waterEvent.regionOfInterestStartDate}</Moment> : "-"}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Region of Interest End Date
                                    </TableCell>
                                    <TableCell>
                                        {waterEvent ? <Moment format="YYYY-MM-DD">{waterEvent.endDate}</Moment> : "-"}
                                    </TableCell>
                                </TableRow>
                                {isDownloadable ?
                                    <TableRow>
                                        <TableCell>
                                            Download
                                    </TableCell>
                                        <TableCell>
                                            {waterEvent ? <DownloadMsgPack eventId={waterEvent._id} /> : "-"}
                                        </TableCell>
                                    </TableRow>
                                    : null}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={12} lg={6}>
                    <h2>Reviewers</h2>
                    <Paper className="paper">
                        <Table>
                            <TableBody>
                                {
                                    waterEvent?.reviewers.map((reviewer, i) => (
                                        <TableRow key={i}>
                                            <TableCell><UserInfo userId={reviewer.reviewer} /></TableCell>
                                            <TableCell><ReviewerStatus status={reviewer.status} /></TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
                <CommentsTable eventId={props.match.params.waterEventId} comments={waterEvent ? waterEvent.comments : []} />
                <Grid item xs={12} md={12} lg={6}>
                    <h2>Data Science</h2>
                    <Paper className="paper">
                        <Table>
                            <TableBody>
                                {
                                    waterEvent?.dataScientists.map((reviewer, i) => (
                                        <TableRow key={i}>
                                            <TableCell><UserInfo userId={reviewer.reviewer} /></TableCell>
                                            <TableCell><ReviewerStatus status={reviewer.status} /></TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
                <TaggingChart eventId={props.match.params.waterEventId} getChartFn={ArtApi.getWaterChart} />
                {waterEvent ?
                    <LabelledEvents
                        reviewers={waterEvent.reviewers}
                        taggedEvents={waterEvent.reviewedEvents}
                        eventId={waterEvent._id}
                        dataScience={false}
                        archived={waterEvent.archived}
                        eventType='Water'
                        completedCallback={closeReview} /> : null}

                {waterEvent ?
                    <LabelledEvents
                        reviewers={waterEvent.dataScientists}
                        taggedEvents={waterEvent.dataScientestReviewedEvents}
                        eventId={waterEvent._id}
                        dataScience={true}
                        archived={waterEvent.archived}
                        eventType='Water'
                        completedCallback={closeReview}/> : null}
            </Grid>
        </Fragment>
    )
}